import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useTheme from 'src/hooks/useTheme';
import useAsyncProcesses from 'src/hooks/useAsyncProcesses';
import { getTenant, patchTenant } from 'src/api/tenants';
import { Button, Space, Form, Input, Tabs, notification } from 'antd';
import RolesEditor from '../../Components/RolesEditor';
import { LuBuilding, LuShieldCheck } from 'react-icons/lu';
import TitleBar from 'src/components/layout/TitleBar';

const TenantEditor = ({ tenantId, onSave }) => {
  const { executeAsyncProcess } = useAsyncProcesses();
  const { theme } = useTheme();

  const [tenant, setTenant] = useState(null);
  const [modified, setModified] = useState(false);

  useEffect(() => {
    if (!tenantId) {
      setTenant(null);
      return;
    }
    if (tenant?.id === tenantId) return;
    setTenant(null);
    loadData();
  }, [tenantId]);

  const loadData = async () => {
    executeAsyncProcess(async () => {
      try {
        setModified(false);
        const resp = await getTenant(tenantId);
        setTenant(resp);
      } catch (error) {
        console.log(error);
      }
    });
  };

  const handleOnChange = (values) => {
    setModified(true);
  };

  const handleOnFinish = (values) => {
    console.log('Success:', values);
    executeAsyncProcess(async () => {
      try {
        const resp = await patchTenant(tenantId, { name: values.name });
        if (onSave) onSave(resp);
        setModified(false);
        notification.success({
          message: 'Success',
          description: <>Data has been saved</>,
          duration: 5,
        });
      } catch (error) {
        console.log(error);
      }
    });
  };

  const handleOnFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
    notification.error({
      message: 'Error',
      description: <>Provided data is invalid</>,
      duration: 5,
    });
  };

  const handleOnReset = () => {
    setModified(false);
  };

  if (!tenant)
    return (
      <div className='h-full flex flex-row justify-center items-center'>
        <div>No data</div>
      </div>
    );

  return (
    <>
      <TitleBar
        busyIndicator={false}
        colorBackground={theme.backgroundBase}
        title={
          <Space className='whitespace-nowrap btn'>
            <LuBuilding className='opacity-60' />
            <div className='overflow-hidden overflow-ellipsis'>{tenant?.name ?? 'unknown'}</div>
          </Space>
        }
        afterTitleExtras={<></>}
      ></TitleBar>
      <div className='p-4'>
        <Tabs
          type='card'
          defaultActiveKey='1'
          items={[
            {
              key: '1',
              label: (
                <Space className='btn'>
                  <LuBuilding />
                  Tenant
                </Space>
              ),
              children: (
                <div>
                  <Form name='tenantEditor' style={{ maxWidth: 600 }} layout='vertical' initialValues={tenant} onChange={handleOnChange} onFinish={handleOnFinish} onFinishFailed={handleOnFinishFailed} onReset={handleOnReset} autoComplete='off'>
                    <Form.Item label='Id' name='id'>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label='Name' name='name' rules={[{ required: true, message: 'The tenant name is required' }]}>
                      <Input disabled={tenant.third_party_provider} />
                    </Form.Item>
                    <Form.Item label='3rd party provider' name='third_party_provider'>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label='3rd party id' name='third_party_id'>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label='3rd party env' name='third_party_env'>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label='Created at' name='created_at'>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label='Updated at' name='updated_at'>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Button htmlType='reset' disabled={!modified}>
                          Reset
                        </Button>
                        <Button type='primary' htmlType='submit' disabled={!modified}>
                          Submit
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                </div>
              ),
            },

            {
              key: '3',
              label: (
                <Space className='btn'>
                  <LuShieldCheck />
                  Roles
                </Space>
              ),
              children: (
                <>
                  <RolesEditor objectType='tenant' id={tenantId} />
                </>
              ),
            },
          ]}
        />
      </div>
    </>
  );
};

TenantEditor.propTypes = {
  tenantId: PropTypes.string,
  onSave: PropTypes.func,
};

export default TenantEditor;
